import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Settings, Cookie } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        // Invalid consent data, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    localStorage.setItem("cookieConsent", JSON.stringify(onlyNecessary));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setPreferences(onlyNecessary);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const cookieCategories = [
    {
      id: "necessary",
      title: "Necessary Cookies",
      description: "Essential for the website to function properly. These cookies enable core functionality such as security, authentication, and session management. Cannot be disabled.",
      required: true
    },
    {
      id: "functional",
      title: "Functional Cookies",
      description: "Enable enhanced functionality and personalization, such as remembering your preferences, language settings, and customization options.",
      required: false
    },
    {
      id: "analytics",
      title: "Analytics Cookies",
      description: "Help us understand how visitors interact with our website by collecting and reporting information anonymously. Used to improve our services.",
      required: false
    },
    {
      id: "marketing",
      title: "Marketing Cookies",
      description: "Used to track visitors across websites to display relevant advertisements and promotional content based on your interests.",
      required: false
    }
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-in slide-in-from-bottom-5 duration-500">
        <div className="max-w-6xl mx-auto bg-card border-2 border-primary/20 rounded-2xl shadow-2xl backdrop-blur-sm">
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold mb-2">We Value Your Privacy</h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or reject non-essential cookies.
                </p>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={handleAcceptAll} 
                    size="sm"
                    className="gap-2"
                  >
                    Accept All
                  </Button>
                  <Button 
                    onClick={handleRejectAll} 
                    variant="outline" 
                    size="sm"
                  >
                    Reject All
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)} 
                    variant="ghost" 
                    size="sm"
                    className="gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Customize
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  By continuing to use our site, you agree to our{" "}
                  <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
                </p>
              </div>

              {/* Close button */}
              <button
                onClick={handleRejectAll}
                className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Manage your cookie preferences below. You can enable or disable different types of cookies according to your preferences.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {cookieCategories.map((category) => (
              <div key={category.id} className="space-y-3 p-4 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Label htmlFor={category.id} className="text-base font-semibold cursor-pointer">
                      {category.title}
                    </Label>
                    {category.required && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        Always Active
                      </span>
                    )}
                  </div>
                  <Switch
                    id={category.id}
                    checked={preferences[category.id]}
                    onCheckedChange={(checked) => {
                      if (!category.required) {
                        setPreferences({ ...preferences, [category.id]: checked });
                      }
                    }}
                    disabled={category.required}
                  />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {category.description}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={handleSavePreferences} className="flex-1">
              Save Preferences
            </Button>
            <Button onClick={handleAcceptAll} variant="outline" className="flex-1">
              Accept All
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
